import { inject, Injectable } from '@angular/core';
import { DrawerService, NotificationsService } from '@pf/shared-ui';
import {
    ContainerFacade,
    EquipmentDto,
    EquipmentFacade,
} from '@pf/platform-wms/domain';
import {
    CpwStagingDrawerComponent,
    CpwStagingDrawerData,
    StagingAssignments,
} from '../components/cpw-staging-drawer.component';
import {
    catchError,
    combineLatest,
    map,
    Observable,
    of,
    take,
    tap,
} from 'rxjs';
import { NotificationType, ToastMessage, ToastType } from '@pf/shared-common';
import { EquipmentStatus, ZoneType } from '@control-tower/platform-wms';
import { uniq } from 'lodash-es';

@Injectable({
    providedIn: 'root',
})
export class CpwStagingService {
    private readonly equipmentFacade = inject(EquipmentFacade);
    private readonly containerFacade = inject(ContainerFacade);
    private readonly drawerService = inject(DrawerService);
    private readonly notificationsService = inject(NotificationsService);
    private drawerOpen = false;

    showDrawer(
        data: { zoneType: ZoneType; equipmentIds: string[] },
        onClose: (success: boolean) => void
    ) {
        if (this.drawerOpen) return;
        this.drawerOpen = true;

        const equipmentIds = uniq(data.equipmentIds ?? []);
        const isUnstaging = data.zoneType === ZoneType.Normal;

        this.equipmentFacade
            .getMany$(equipmentIds, true)
            .pipe(
                take(1),
                map(results => results.filter((eq): eq is EquipmentDto => !!eq))
            )
            .subscribe(equipment => {
                const drawerData: CpwStagingDrawerData = {
                    ...data,
                    equipmentIds,
                    equipment,
                };

                this.drawerService.createTemplateDrawer({
                    title: isUnstaging
                        ? 'Move to Available'
                        : 'Move to Staging',
                    content: CpwStagingDrawerComponent,
                    onClose: (assignments?: StagingAssignments) => {
                        this.drawerOpen = false;
                        if (
                            assignments &&
                            Object.keys(assignments).length > 0
                        ) {
                            const targetStatus = isUnstaging
                                ? EquipmentStatus.Available
                                : EquipmentStatus.Staged;
                            const successMessage = isUnstaging
                                ? 'Equipment moved to available'
                                : 'Equipment moved to staging area';
                            const errorMessage = isUnstaging
                                ? 'Failed to move equipment to available'
                                : 'Failed to move equipment to staging area';
                            this.processStagingAssignments$(
                                assignments,
                                targetStatus
                            )
                                .pipe(
                                    take(1),
                                    tap(success => {
                                        if (success) {
                                            this.notificationsService.create({
                                                type: NotificationType.ToastMessage,
                                                toastType: ToastType.success,
                                                message: successMessage,
                                            } as ToastMessage);
                                        } else {
                                            this.notificationsService.create({
                                                type: NotificationType.ToastMessage,
                                                toastType: ToastType.error,
                                                message: errorMessage,
                                            } as ToastMessage);
                                        }
                                        onClose(success);
                                    })
                                )
                                .subscribe();
                        } else {
                            onClose(false);
                        }
                    },
                    size: 'default',
                    isMobile: true,
                    data: drawerData,
                });
            });
    }

    private processStagingAssignments$(
        assignments: StagingAssignments,
        status: EquipmentStatus
    ) {
        const ops$: Observable<boolean>[] = [];
        const processedEquipment = new Set<string>();

        for (const [itemId, assignment] of Object.entries(assignments)) {
            if (
                assignment.isLtl &&
                assignment.slotId &&
                assignment.equipmentId
            ) {
                // LTL: assign container to the scanned slot
                ops$.push(
                    this.containerFacade
                        .assignSlot$(itemId, assignment.slotId)
                        .pipe(
                            map(() => true),
                            catchError(() => of(false))
                        )
                );

                // Track which equipment needs to be updated
                processedEquipment.add(assignment.equipmentId);
            } else if (assignment.zoneId) {
                // Non-LTL: move equipment to the scanned zone
                const equipment = this.equipmentFacade.getById(itemId);
                if (!equipment) {
                    ops$.push(of(false));
                    continue;
                }

                equipment.zone = { id: assignment.zoneId };
                equipment.status = status;
                ops$.push(
                    this.equipmentFacade.save$(equipment).pipe(
                        map(() => true),
                        catchError(() => of(false))
                    )
                );
            }
        }

        // Update status for all LTL equipment that had containers assigned
        for (const equipmentId of processedEquipment) {
            const equipment = this.equipmentFacade.getById(equipmentId);
            if (equipment) {
                equipment.status = EquipmentStatus.Available;
                ops$.push(
                    this.equipmentFacade.save$(equipment).pipe(
                        map(() => true),
                        catchError(() => of(false))
                    )
                );
            }
        }

        if (ops$.length === 0) {
            return of(true);
        }

        return combineLatest(ops$).pipe(
            map(results => results.every(result => result)),
            catchError(() => of(false))
        );
    }
}
